import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, MapPin, Phone, Linkedin, Instagram, Facebook, Youtube, Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS, COMPANY_INFO } from "@/data/site";
import { Button } from "@/components/ui/button";
import { useState } from "react";

function Logo({ size = "md" }) {
  const sz = size === "sm" ? "w-8 h-8" : "w-10 h-10";
  return (
    <Link to="/" className="flex items-center gap-2.5" data-testid="site-logo">
      <div className={`${sz} bg-[#2563EB] hex-clip flex items-center justify-center relative`}>
        <div className={`${sz} scale-[0.7] bg-[#0A2540] hex-clip absolute inset-0 m-auto`} />
        <span className="relative text-white font-extrabold text-lg z-10" style={{ fontFamily: "Outfit" }}>E</span>
      </div>
      <div className="leading-tight">
        <div className="font-extrabold tracking-tight text-[#0A2540] text-[15px]" style={{ fontFamily: "Outfit" }}>EASYWORKS</div>
        <div className="text-[9px] tracking-[0.22em] text-slate-500 font-semibold">INDUSTRIES PVT. LTD.</div>
      </div>
    </Link>
  );
}

export default function Header() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-slate-200" data-testid="site-header">
      {/* Utility bar */}
      <div className="hidden md:block bg-white border-b border-slate-100">
        <div className="container mx-auto max-w-7xl px-6 flex items-center justify-between text-[13px] text-slate-600 py-2.5">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#2563EB]" />Head Office: India</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#2563EB]" />Regional Office: UAE</span>
            <a href={`mailto:${COMPANY_INFO.email}`} className="flex items-center gap-1.5 hover:text-[#2563EB] transition-colors"><Mail className="w-3.5 h-3.5 text-[#2563EB]" />{COMPANY_INFO.email}</a>
            <a href={`tel:${COMPANY_INFO.phoneDial}`} className="flex items-center gap-1.5 hover:text-[#2563EB] transition-colors"><Phone className="w-3.5 h-3.5 text-[#2563EB]" />IN {COMPANY_INFO.phone}</a>
            <a href={`tel:${COMPANY_INFO.phoneUAEDial}`} className="flex items-center gap-1.5 hover:text-[#2563EB] transition-colors"><Phone className="w-3.5 h-3.5 text-[#2563EB]" />UAE {COMPANY_INFO.phoneUAE}</a>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-slate-500">Follow Us:</span>
            <a href={COMPANY_INFO.socials.linkedin} className="w-7 h-7 rounded-full bg-[#0A66C2] text-white flex items-center justify-center hover:opacity-90 transition-opacity"><Linkedin className="w-3.5 h-3.5" /></a>
            <a href={COMPANY_INFO.socials.instagram} className="w-7 h-7 rounded-full bg-gradient-to-br from-fuchsia-500 to-orange-400 text-white flex items-center justify-center hover:opacity-90 transition-opacity"><Instagram className="w-3.5 h-3.5" /></a>
            <a href={COMPANY_INFO.socials.facebook} className="w-7 h-7 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-90 transition-opacity"><Facebook className="w-3.5 h-3.5" /></a>
            <a href={COMPANY_INFO.socials.youtube} className="w-7 h-7 rounded-full bg-[#FF0000] text-white flex items-center justify-center hover:opacity-90 transition-opacity"><Youtube className="w-3.5 h-3.5" /></a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="container mx-auto max-w-7xl px-6 flex items-center justify-between h-[76px]">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => {
            const active = pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.to.replace(/\//g, "") || "home"}`}
                className={`relative px-3.5 py-2 text-[14px] font-medium transition-colors ${active ? "text-[#2563EB]" : "text-slate-700 hover:text-[#2563EB]"}`}
              >
                {link.label}
                {active && <span className="absolute bottom-0 left-3.5 right-3.5 h-[2.5px] bg-[#2563EB] rounded-full" />}
                {["Companies", "Services", "Industries", "Resources"].includes(link.label) && (
                  <ChevronDown className="inline-block w-3 h-3 ml-0.5 opacity-60" />
                )}
              </Link>
            );
          })}
        </nav>
        <div className="hidden lg:block">
          <Link to="/contact" data-testid="header-cta">
            <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-11 px-5 rounded-md font-semibold shadow-md shadow-blue-600/20 transition-colors">
              Book Free Consultation
            </Button>
          </Link>
        </div>
        <button
          className="lg:hidden p-2 text-slate-700"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="container mx-auto max-w-7xl px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className={`px-3 py-2.5 rounded-md text-[15px] font-medium ${pathname === link.to ? "bg-blue-50 text-[#2563EB]" : "text-slate-700 hover:bg-slate-50"}`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/contact" onClick={() => setOpen(false)} className="mt-2">
              <Button className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white">Book Free Consultation</Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
